import { CreativeAd, CreativeAdDetail } from '../types'
import { getPreviousMonth } from './months'

/**
 * Locale-aware "Mar 5" formatter. Memoised at module scope because the
 * tooltip mounts on hover and we don't need a fresh formatter per render.
 */
const SHORT_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' })

/**
 * Pre-computed values rendered inside the creative-ad tooltip. Keeping the
 * derivation in a helper means the table and card-grid views stay in sync —
 * both call this with the same inputs and render the same content.
 */
export interface CreativeAdTooltipData {
  /** 0-100 install share relative to the strongest performer in scope. */
  performancePct: number
  /**
   * Months in which the ad was present (counts the whole `monthly` map). `0`
   * for listing rows, which don't carry the per-month presence map.
   */
  monthsActive: number
  /** True when the ad is present this month but wasn't in the prior month. */
  isNewThisMonth: boolean
  /** Localised "Mar 5"-style label for `firstSeenAt`, or `''` when missing. */
  firstSeenLabel: string
}

/**
 * Builds the tooltip payload from a single `CreativeAd` plus the active
 * month/scope context. Pure, no React deps — safe to call inside `useMemo`.
 */
export function computeCreativeAdTooltipData(
  ad: CreativeAd & Partial<Pick<CreativeAdDetail, 'monthly'>>,
  currentMonth: string | null,
  maxInstallShare: number,
  months: readonly string[]
): CreativeAdTooltipData {
  const performancePct = maxInstallShare > 0 ? Math.min(100, ((ad.installShare ?? 0) / maxInstallShare) * 100) : 0
  const monthly = ad.monthly ?? {}
  const current = currentMonth ? monthly[currentMonth] : undefined
  const prevMonth = getPreviousMonth(months, currentMonth)
  const prev = prevMonth ? monthly[prevMonth] : undefined

  // Treat the `present` flag as the source of truth — a `monthly` entry can
  // exist with `present: false` (e.g. an ad seen the month before but not this
  // one) and we don't want those counted as active months.
  const monthsActive = Object.values(monthly).filter((m) => m.present).length

  let firstSeenLabel = ''
  if (ad.firstSeenAt) {
    const parsed = new Date(ad.firstSeenAt)
    if (!Number.isNaN(parsed.getTime())) firstSeenLabel = SHORT_DATE_FORMATTER.format(parsed)
  }

  return {
    performancePct,
    monthsActive,
    // "New this month" is only meaningful when we have a previous month to
    // compare against — otherwise every ad in the earliest month would falsely
    // light up as "new".
    isNewThisMonth: Boolean(currentMonth && prevMonth && current?.present && !prev?.present),
    firstSeenLabel,
  }
}
